import { stoxlyClient, updateAccessToken } from '@/lib/stoxly';
import { apiErrorMessage } from '@/utils/api-error';
import { runWithBootRetry } from '@/lib/boot-retry';
import { supabase } from '@/lib/supabase';
import router from '@/router';
import type { User } from '@/types/user';
import { authStore } from '@/lib/auth-store';

const syncWithBackend = async (accessToken: string) => {
  updateAccessToken(accessToken);
  const { user: me } = await stoxlyClient.authGetMe();
  // A SIGNED_OUT that landed while /auth/me was in flight already cleared the
  // state and token cache — applying this result would resurrect a signed-out
  // session. Discard it.
  if (!authStore.store.state.session) return;
  authStore.setState((s) => ({ ...s, user: me as User }));
};

const clearAuthState = () => {
  authStore.setState((s) => ({
    ...s,
    user: null,
    session: null,
    error: null,
    bootAttempt: 0,
    bootFailed: false,
  }));
  updateAccessToken(null);
};

/** Admin-only panel: a synced non-admin is rejected outright. */
const enforceAdminRole = async (): Promise<boolean> => {
  if (authStore.store.state.user?.role === 'admin') return true;
  clearAuthState();
  await supabase.auth.signOut();
  authStore.setState((s) => ({ ...s, error: 'Access denied. Admin privileges required.' }));
  router.push('/login');
  return false;
};

// Single-flight: INITIAL_SESSION and SIGNED_IN can both fire at boot (OAuth
// callback, cross-tab sign-in) — only one retry ladder may run at a time.
let bootSync: Promise<void> | null = null;

const runBootSync = (): Promise<void> => {
  bootSync ??= (async () => {
    authStore.setState((s) => ({ ...s, bootFailed: false }));
    const outcome = await runWithBootRetry(
      // Re-read the LIVE session each attempt: Supabase may have refreshed the
      // token mid-ladder, and a SIGNED_OUT mid-ladder must ABORT — a captured
      // boot-time token would keep authenticating a revoked session.
      () => {
        const token = authStore.store.state.session?.access_token;
        if (!token) {
          throw new Error('Signed out during boot sync');
        }
        return syncWithBackend(token);
      },
      {
        onAttempt: (attempt) => {
          authStore.setState((s) => ({ ...s, bootAttempt: attempt }));
        },
      },
    );
    authStore.setState((s) => ({ ...s, bootAttempt: 0 }));

    switch (outcome.status) {
      case 'ok':
        // Signed out while the last attempt was in flight: SIGNED_OUT owns state
        // + redirect — enforcing the role on a wiped user would flash a bogus
        // "Access denied".
        if (!authStore.store.state.session) break;
        if (await enforceAdminRole()) {
          authStore.setState((s) => ({ ...s, error: null }));
          if (router.currentRoute.value.path === '/login') {
            router.push('/');
          }
        }
        break;
      case 'unauthorized':
        // The backend definitively rejected the token (post-refresh 401): clear
        // LOCAL state only — a global signOut would cascade to every open tab —
        // and land on the login screen.
        clearAuthState();
        router.push('/login');
        break;
      case 'failed':
        // Signed out mid-ladder: SIGNED_OUT already cleared state and owns the
        // redirect — never a failed-boot panel over a deliberate logout.
        if (!authStore.store.state.session) break;
        console.error('[Admin Auth] Boot sync failed:', outcome.error);
        // With an already-synced user this was a redundant re-sync — keep the
        // established state. Otherwise surface the failed-boot panel; the session
        // is KEPT so Retry can succeed without a re-login.
        if (!authStore.store.state.user) {
          authStore.setState((s) => ({
            ...s,
            error: apiErrorMessage(outcome.error),
            bootFailed: true,
          }));
        }
        break;
    }
  })().finally(() => {
    bootSync = null;
  });
  return bootSync;
};

export const initializeAuth = (): void => {
  authStore.setState((s) => ({ ...s, isInitializing: true }));

  // The listener is registered FIRST and unconditionally: Supabase v2 fires
  // INITIAL_SESSION immediately with the stored session, and a failed boot sync
  // must never cost auth its recovery path (TOKEN_REFRESHED et al).
  supabase.auth.onAuthStateChange((event, currentSession) => {
    console.debug('[Admin Auth] State change:', event);
    authStore.setState((s) => ({ ...s, session: currentSession }));

    switch (event) {
      case 'INITIAL_SESSION':
        if (currentSession) {
          void runBootSync().finally(() => {
            authStore.setState((s) => ({ ...s, isInitializing: false }));
          });
        } else {
          authStore.setState((s) => ({ ...s, isInitializing: false }));
        }
        break;

      case 'SIGNED_IN':
        if (currentSession) {
          void runBootSync();
        }
        break;

      case 'TOKEN_REFRESHED':
      case 'USER_UPDATED':
        if (currentSession) {
          // Swallow failures: Supabase keeps refreshing on its own and the next
          // event re-syncs — never sign out over one missed sync.
          syncWithBackend(currentSession.access_token).catch((err) => {
            console.error('[Admin Auth] Failed to sync:', err);
          });
        }
        break;

      case 'SIGNED_OUT':
        clearAuthState();
        router.push('/login');
        break;
    }
  });
};

/** Re-run the boot ladder from the failed-boot panel (session was kept). */
export const retryBoot = async (): Promise<void> => {
  if (!authStore.store.state.session?.access_token) {
    clearAuthState();
    router.push('/login');
    return;
  }
  await runBootSync();
};

export const login = async (email: string, password: string) => {
  authStore.setState((s) => ({ ...s, isLoading: true, error: null }));

  try {
    const { data, error: authError } = await supabase.auth.signInWithPassword({
      email,
      password,
    });

    if (authError) {
      throw new Error(authError.message);
    }

    if (data.session) {
      authStore.setState((s) => ({ ...s, session: data.session }));
      await syncWithBackend(data.session.access_token);

      if (authStore.store.state.user?.role !== 'admin') {
        clearAuthState();
        await supabase.auth.signOut();
        throw new Error('Access denied. Admin privileges required.');
      }
    }

    return data;
  } catch (err) {
    const errorMessage = err instanceof Error ? err.message : 'Login failed';
    authStore.setState((s) => ({ ...s, error: errorMessage }));
    throw err;
  } finally {
    authStore.setState((s) => ({ ...s, isLoading: false }));
  }
};

export const logout = async (): Promise<void> => {
  try {
    await supabase.auth.signOut();
    clearAuthState();
    router.push('/login');
  } catch (err) {
    const errorMessage = err instanceof Error ? err.message : 'Logout failed';
    authStore.setState((s) => ({ ...s, error: errorMessage }));
    throw err;
  }
};
